/**
 * <s-variant-media> — Moves the selected variant's image to the front of the product gallery.
 *
 * Required child markup:
 *   [data-product-gallery]                  — The gallery track whose tiles are reordered.
 *   [data-media-id]                         — On each gallery tile; the Shopify media id.
 *   script[data-variant-media-map]          — JSON object { variantId: mediaId } for variants with an image.
 *
 * External coupling:
 *   s-variant-picker option inputs carry [data-variant-id] (or select > option[data-variant-id]);
 *   this element listens for their bubbling `change` events on the document because the picker
 *   is re-rendered inside the product-form partial on every selection.
 */
class VariantMedia extends HTMLElement {
  #ac;
  #map = {};

  connectedCallback() {
    this.#ac = new AbortController();
    try {
      this.#map = JSON.parse(this.querySelector('script[data-variant-media-map]')?.textContent || '{}');
    } catch {
      this.#map = {};
    }
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement) || !input.closest('s-variant-picker')) return;
      const variantId = input.dataset.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
      if (variantId) this.#show(variantId);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #show(variantId) {
    const mediaId = this.#map[variantId];
    const gallery = this.querySelector('[data-product-gallery]');
    if (!mediaId || !gallery) return;
    const tile = gallery.querySelector(`[data-media-id="${mediaId}"]`);
    if (!tile) return;
    if (gallery.firstElementChild !== tile) gallery.prepend(tile);
    const image = tile.querySelector('img[loading="lazy"]');
    if (image) image.loading = 'eager';
  }
}

if (!customElements.get('s-variant-media')) {
  customElements.define('s-variant-media', VariantMedia);
}
